import React, { useMemo, useState } from "react";
import "../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../components/buttons/SecondaryButton";
import PrimaryButton from "../components/buttons/PrimaryButton";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../components/CustomSelect";

const initialState = {
  numberOfApplicants: "",
  applicant1Income: "",
  applicant2Income: "",
  committedMonthlySpending: "",
  reasonForMortgage: "",
  deposit: "",
  propertyValue: "",
};

const applicantOptions = [
  { value: "", label: "Please select number of applicant(s)" },
  { value: "1", label: "One Person" },
  { value: "2", label: "Two People" },
];

const reasonOptions = [
  { value: "", label: "Select reason for mortgage" },
  { value: "first_home", label: "First Home" },
  { value: "new_home", label: "New Home" },
  { value: "second_home", label: "Second Home" },
  { value: "remortgage", label: "Remortgage" },
  { value: "mortgage_owned_property", label: "Mortgage Owned Property" },
];

const sanitizeNumber = (value) => String(value || "").replace(/[^\d.]/g, "");

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const getLtvFromReason = (reason) => {
  switch (reason) {
    case "first_home":
    case "new_home":
      return 85;
    case "second_home":
      return 80;
    case "remortgage":
    case "mortgage_owned_property":
      return 69;
    default:
      return 0;
  }
};

const isMortgageValueReason = (reason) => {
  return ["remortgage", "mortgage_owned_property"].includes(reason);
};

const isDepositReason = (reason) => {
  return ["first_home", "new_home", "second_home"].includes(reason);
};

export default function HowMuchCanIBorrowCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [errors, setErrors] = useState([]);

  const applicant1IncomeNum = Number(formData.applicant1Income || 0);
  const applicant2IncomeNum =
    formData.numberOfApplicants === "2"
      ? Number(formData.applicant2Income || 0)
      : 0;
  const committedMonthlySpendingNum = Number(
    formData.committedMonthlySpending || 0,
  );
  const depositNum = Number(formData.deposit || 0);
  const propertyValueNum = Number(formData.propertyValue || 0);

  const totalIncome = applicant1IncomeNum + applicant2IncomeNum;
  const ltv = getLtvFromReason(formData.reasonForMortgage);

  const requiresPropertyValue = isMortgageValueReason(
    formData.reasonForMortgage,
  );
  const usesDeposit = isDepositReason(formData.reasonForMortgage);

  const estimatedBorrowAmount = useMemo(() => {
    if (!hasCalculated) return 0;

    if (requiresPropertyValue) {
      return propertyValueNum * (ltv / 100);
    }

    const annualCommittedSpending = committedMonthlySpendingNum * 12;
    const disposableIncome = totalIncome - annualCommittedSpending;
    const result = disposableIncome * 0.5;

    return result > 0 ? result : 0;
  }, [
    hasCalculated,
    requiresPropertyValue,
    propertyValueNum,
    ltv,
    committedMonthlySpendingNum,
    totalIncome,
  ]);

  const roughPropertyBudget = useMemo(() => {
    if (!hasCalculated) return 0;

    if (usesDeposit) {
      return estimatedBorrowAmount + depositNum;
    }

    return propertyValueNum;
  }, [
    hasCalculated,
    usesDeposit,
    estimatedBorrowAmount,
    depositNum,
    propertyValueNum,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const validate = () => {
    const nextErrors = [];

    if (!formData.numberOfApplicants) {
      nextErrors.push("Please select the number of applicants.");
    }

    if (!applicant1IncomeNum || applicant1IncomeNum <= 0) {
      nextErrors.push("Please enter applicant 1 income.");
    }

    if (
      formData.numberOfApplicants === "2" &&
      (!applicant2IncomeNum || applicant2IncomeNum <= 0)
    ) {
      nextErrors.push("Please enter applicant 2 income.");
    }

    if (
      !formData.committedMonthlySpending &&
      formData.committedMonthlySpending !== "0"
    ) {
      nextErrors.push("Please enter committed monthly spending.");
    }

    if (!formData.reasonForMortgage) {
      nextErrors.push("Please select a reason for mortgage.");
    }

    if (requiresPropertyValue && (!propertyValueNum || propertyValueNum <= 0)) {
      nextErrors.push("Please enter the property value.");
    }

    if (usesDeposit && depositNum < 0) {
      nextErrors.push("Deposit cannot be less than 0.");
    }

    setErrors(nextErrors);
    return nextErrors.length === 0;
  };

  const handleCalculate = () => {
    const isValid = validate();
    setHasCalculated(isValid);
  };

  const handleClear = () => {
    setFormData(initialState);
    setErrors([]);
    setHasCalculated(false);
  };

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title primary">HOW MUCH CAN I BORROW?</div>

        {errors.length > 0 && (
          <div
            style={{
              background: "#ffffff",
              marginBottom: "24px",
              border: "1px solid #d9d9d9",
            }}
          >
            <div
              style={{
                background: "var(--secondaryColor)",
                color: "#fff",
                padding: "12px 16px",
                fontWeight: 700,
                fontSize: "18px",
              }}
            >
              Error!
            </div>
            <div style={{ padding: "16px 20px", color: "var(--primaryColor)" }}>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {errors.map((error, index) => (
                  <li key={index} style={{ marginBottom: "8px" }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="borrow-field-group">
          <label className="borrow-label">Number of Applicant(s)</label>
          <div className="select-wrapper">
            {/* <select
              name="numberOfApplicants"
              className="borrow-input"
              value={formData.numberOfApplicants}
              onChange={handleChange}
            >
              {applicantOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="numberOfApplicants"
              options={applicantOptions}
              value={formData.numberOfApplicants}
              onChange={handleChange}
              placeholder="Please select number of applicant(s)"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Applicant 1 Income (Annually)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="applicant1Income"
              value={formData.applicant1Income}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  applicant1Income: raw,
                }))
              }
              className="borrow-input"
            />
          </div>
        </div>

        {formData.numberOfApplicants === "2" && (
          <div className="borrow-field-group">
            <label className="borrow-label">
              Applicant 2 Income (Annually)
            </label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="applicant2Income"
                value={formData.applicant2Income}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    applicant2Income: raw,
                  }))
                }
                className="borrow-input"
              />
            </div>
          </div>
        )}

        <div className="borrow-field-group">
          <label className="borrow-label">Committed Monthly Spending (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="committedMonthlySpending"
              value={formData.committedMonthlySpending}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  committedMonthlySpending: raw,
                }))
              }
              className="borrow-input"
            />
          </div>
          <div className="borrow-helper-text">
            The amount you spend to repay credit and store cards, catalogue
            purchases, loans, overdrafts, maintenance and your pension. You do
            not need to tell us about general household spending, such as
            groceries, travel and utility bills.
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Reason</label>
          <div className="select-wrapper">
            <CustomSelect
              name="reasonForMortgage"
              options={reasonOptions}
              value={formData.reasonForMortgage}
              onChange={handleChange}
              placeholder="Please select a reason"
            />
          </div>
        </div>

        {requiresPropertyValue && (
          <div className="borrow-field-group">
            <label className="borrow-label">Property value (£)</label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="propertyValue"
                value={formData.propertyValue}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    propertyValue: raw,
                  }))
                }
                className="borrow-input"
              />
            </div>
          </div>
        )}

        {usesDeposit && (
          <div className="borrow-field-group">
            <label className="borrow-label">Deposit (£)</label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="deposit"
                value={formData.deposit}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    deposit: raw,
                  }))
                }
                className="borrow-input"
              />
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {hasCalculated && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <div className="result-section">
              <p className="result-copy">
                Based on your annual income of{" "}
                <strong>{formatCurrency(totalIncome)}</strong>, and monthly
                expenditure of{" "}
                <strong>{formatCurrency(committedMonthlySpendingNum)}</strong>{" "}
                you may be able to borrow up to:
              </p>

              <div className="result-value">
                {formatCurrency(estimatedBorrowAmount)}
              </div>

              {usesDeposit ? (
                <>
                  <p className="result-copy">
                    adding your deposit of{" "}
                    <strong>{formatCurrency(depositNum)}</strong> gives you a
                    rough property budget of:
                  </p>

                  <div className="result-value">
                    {formatCurrency(roughPropertyBudget)}
                  </div>
                </>
              ) : (
                <>
                  <p className="result-copy">
                    based on a property value of{" "}
                    <strong>{formatCurrency(propertyValueNum)}</strong>.
                  </p>
                </>
              )}

              <p className="result-copy">
                at a loan to value of <strong>{ltv}%</strong>.
              </p>
            </div>

            <div className="result-divider" />

            <div className="result-section" style={{ marginBottom: 0 }}>
              <div className="result-heading">PLEASE NOTE:</div>
              <p className="result-copy">
                This is only an estimate and does not guarantee mortgage
                approval. The amount you may be able to borrow will depend on
                your individual circumstances and lender criteria. Any figures
                provided should be used as a guide only and may form part of
                your financial planning process.
              </p>
              <p className="result-copy">
                This mortgage repayment calculator is an interactive tool which
                should be used for your guidance only, and must not form part of
                your financial decision making process.
              </p>
              <p className="result-copy">
                Your home may be repossessed if you do not keep up repayments on
                a mortgage or any other debt secured on it.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
